<template>
  <a-modal
    v-model:visible="modalVisible"
    :footer="null"
    destroyOnClose
    title="Create Account"
    :mask-closable="false"
  >
    <a-form
      v-if="!emailSent"
      layout="vertical"
      :model="registerForm"
      :rules="rules"
      @finish="handleRegister"
    >
      <a-form-item name="email" label="Email" validateFirst>
        <a-input
          v-model:value="registerForm.email"
          :disabled="isLoading"
          size="large"
          placeholder="Email"
        />
      </a-form-item>
      <a-form-item name="password" label="Password" validateFirst>
        <a-input-password
          v-model:value="registerForm.password"
          :disabled="isLoading"
          size="large"
          placeholder="Password"
          autocomplete="off"
        />
      </a-form-item>
      <a-form-item name="firstName" label="First Name">
        <a-input
          v-model:value="registerForm.firstName"
          :disabled="isLoading"
          size="large"
          placeholder="First Name"
        />
      </a-form-item>
      <a-form-item name="lastName" label="Last Name">
        <a-input
          v-model:value="registerForm.lastName"
          :disabled="isLoading"
          size="large"
          placeholder="Last Name"
        />
      </a-form-item>

      <a-form-item>
        <vue-recaptcha
          ref="captcha"
          sitekey="6LfQ5kgjAAAAAOswLUMLdhmd_X--iXFZUhIumlvt"
          @verify="recaptcha"
          @expired="recaptcha('')"
        />
      </a-form-item>

      <a-form-item>
        <a-button
          type="primary"
          html-type="submit"
          :loading="isLoading"
          :disabled="isLoading || registerForm.recaptcha === ''"
        >
          Register
        </a-button>
      </a-form-item>
    </a-form>
    <div v-else>
      <div class="check-email">
        <template v-if="nativeEnabled">
          If this email can be registered, check your inbox for confirmation. If no email arrives,
          try registering again or sign in. Contact Allume support if you need help.
        </template>
        <template v-else>
          We’ve sent an email to your inbox. Please check and follow the instructions in it.
        </template>
      </div>
      <div>
        Not receiving the email?
        <span v-if="countdown === 0">
          Click <a class="resend" @click="resend">here</a> to resend
        </span>
        <span v-else>Resend after {{ countdown }} seconds</span>
      </div>
    </div>
  </a-modal>
</template>

<script>
import {
  validateEmailAddress,
  validateStrongPassword,
  validateCognitoPassword,
} from '@/utils/validators';
import { VueRecaptcha } from 'vue-recaptcha';
import { isNativeCognitoEnabled } from '@/shared/auth/cognitoFeatureFlag';
import { registerNative, resendNative, nativeAuthMessage } from '@/shared/auth/nativeCognito';

export default {
  name: 'RegisterModal',
  props: {
    visible: Boolean,
    confirmationEmail: { type: String, default: '' },
  },
  components: { VueRecaptcha },
  data() {
    return {
      isLoading: false,
      emailSent: false,
      countdown: 0,
      resendAvailable: false,
      nativeEnabled: isNativeCognitoEnabled,
      countdownTimer: null,
      operationVersion: 0,
      registerForm: {
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        recaptcha: '',
      },
      rules: {
        email: [
          { required: true, message: 'Email is required', trigger: 'blur' },
          {
            validator: validateEmailAddress,
            trigger: 'blur',
          },
        ],
        password: [
          { required: true, message: 'Password is required', trigger: 'blur' },
          {
            validator: isNativeCognitoEnabled ? validateCognitoPassword : validateStrongPassword,
            trigger: 'blur',
          },
        ],
        firstName: [
          {
            required: true,
            message: 'First Name is required',
            trigger: 'blur',
          },
        ],
        lastName: [{ required: true, message: 'Last Name is required', trigger: 'blur' }],
      },
    };
  },
  computed: {
    modalVisible: {
      get() {
        return this.visible;
      },
      set(visible) {
        this.$emit('update:visible', visible);
      },
    },
  },
  watch: {
    visible: {
      immediate: true,
      handler(visible) {
        if (!isNativeCognitoEnabled) return;
        this.operationVersion++;
        this.isLoading = false;
        this.registerForm.password = '';
        if (this.countdownTimer) clearInterval(this.countdownTimer);
        this.countdown = 0;
        if (visible) {
          this.emailSent = !!this.confirmationEmail;
          if (this.confirmationEmail) this.registerForm.email = this.confirmationEmail;
        }
      },
    },
  },
  beforeUnmount() {
    this.operationVersion++;
    if (this.countdownTimer) clearInterval(this.countdownTimer);
  },
  methods: {
    async resend() {
      if (isNativeCognitoEnabled) {
        if (this.isLoading || this.countdown > 0) return;
        this.isLoading = true;
        const version = this.operationVersion;
        try {
          await resendNative(this.registerForm.email);
          if (version !== this.operationVersion) return;
          this.countDown();
          this.$message.success('Check your inbox for the confirmation email.');
        } catch (error) {
          if (version === this.operationVersion) this.$message.error(nativeAuthMessage(error));
        } finally {
          if (version === this.operationVersion) this.isLoading = false;
        }
        return;
      }
      this.countDown();
      try {
        await this.$http.post(this.$api.registerResend, {
          email: this.registerForm.email,
        });
        this.$message.success(
          'We have sent an Email to you. Please check your email inbox for further instructions',
        );
      } catch (e) {
        this.$message.error(e);
      }
    },
    countDown() {
      if (this.countdownTimer) clearInterval(this.countdownTimer);
      this.countdown = 30;
      this.countdownTimer = setInterval(() => {
        if (this.countdown > 0) {
          this.countdown--;
        }
        if (this.countdown === 0) {
          clearInterval(this.countdownTimer);
        }
      }, 1000);
    },
    async recaptcha(token) {
      this.registerForm.recaptcha = token;
    },
    async handleRegister() {
      if (this.isLoading) return;
      this.isLoading = true;
      if (isNativeCognitoEnabled) {
        const version = this.operationVersion;
        const input = { ...this.registerForm };
        try {
          await registerNative(input);
          if (version !== this.operationVersion) return;
          this.registerForm.email = input.email;
          this.emailSent = true;
          this.countDown();
        } catch (error) {
          if (version === this.operationVersion) {
            this.$message.error(nativeAuthMessage(error));
            this.registerForm.recaptcha = '';
            this.$refs.captcha?.reset?.();
          }
        } finally {
          if (version === this.operationVersion) {
            this.registerForm.password = '';
            this.isLoading = false;
          }
        }
        return;
      }
      try {
        await this.$http.post(this.$api.register, this.registerForm);
        this.$message.success(
          'We have sent an Email to you. Please check your email inbox for further instructions',
        );
        this.emailSent = true;
      } catch (e) {
        this.$message.error(e);
      }
      this.isLoading = false;
    },
  },
};
</script>

<style scoped>
.check-email {
  margin-bottom: 10px;
}
</style>
